import { useCallback } from 'react';

import { Currency, SupportedCurrency } from '__generated__/globalTypes';
import { useConfigContext } from 'contexts/config';
import { useCurrentUserContext } from 'contexts/currentUser';
import { MonetaryAmount, MonetaryAmountParams } from 'lib/monetaryAmount';

export const Sum = (amounts: MonetaryAmount[]) => {
  return amounts.reduce((acc, amount) => {
    return new MonetaryAmount({
      eurCents: acc.eurCents + amount.eurCents,
      usdCents: acc.usdCents + amount.usdCents,
      gbpCents: acc.gbpCents + amount.gbpCents,
      wei: acc.wei + amount.wei,
      referenceCurrency: acc.referenceCurrency,
    });
  }, MonetaryAmount.zero);
};

export const useMonetaryAmount = () => {
  const { exchangeRate } = useConfigContext();
  const {
    fiatCurrency: { code },
    currency,
  } = useCurrentUserContext();

  const toMonetaryAmount = useCallback(
    (params: MonetaryAmountParams): MonetaryAmount => {
      return MonetaryAmount.fromRates(params, exchangeRate.ethRates);
    },
    [exchangeRate.ethRates]
  );

  const toMonetaryAmountInReferenceCurrency = useCallback(
    (
      amount: number | bigint | string,
      referenceCurrency: SupportedCurrency
    ): MonetaryAmount => {
      return MonetaryAmount.fromReferenceCurrency(
        referenceCurrency,
        amount,
        exchangeRate.ethRates
      );
    },
    [exchangeRate.ethRates]
  );

  const eur = useCallback(
    (amount: number): MonetaryAmount => {
      return toMonetaryAmountInReferenceCurrency(amount, SupportedCurrency.EUR);
    },
    [toMonetaryAmountInReferenceCurrency]
  );

  const usd = useCallback(
    (amount: number): MonetaryAmount => {
      return toMonetaryAmountInReferenceCurrency(amount, SupportedCurrency.USD);
    },
    [toMonetaryAmountInReferenceCurrency]
  );

  const gbp = useCallback(
    (amount: number): MonetaryAmount => {
      return toMonetaryAmountInReferenceCurrency(amount, SupportedCurrency.GBP);
    },
    [toMonetaryAmountInReferenceCurrency]
  );

  const wei = useCallback(
    (amount: bigint | number | string): MonetaryAmount => {
      return toMonetaryAmountInReferenceCurrency(
        BigInt(amount),
        SupportedCurrency.WEI
      );
    },
    [toMonetaryAmountInReferenceCurrency]
  );

  const toMonetaryAmountInFiatCurrency = useCallback(
    (amount: number): MonetaryAmount => {
      return toMonetaryAmountInReferenceCurrency(amount, code);
    },
    [toMonetaryAmountInReferenceCurrency, code]
  );

  const getUserFiatAmountInMajorUnit = useCallback(
    (monetaryAmount: MonetaryAmount) => monetaryAmount.toMajorUnit(code),
    [code]
  );

  const getUserAmount = useCallback(
    (monetaryAmount: MonetaryAmount) =>
      currency === Currency.FIAT
        ? getUserFiatAmountInMajorUnit(monetaryAmount)
        : monetaryAmount.eth,
    [currency, getUserFiatAmountInMajorUnit]
  );

  return {
    toMonetaryAmount,
    toMonetaryAmountInFiatCurrency,
    getUserFiatAmountInMajorUnit,
    getUserAmount,
    toMonetaryAmountInReferenceCurrency,
    eur,
    usd,
    gbp,
    wei,
  };
};
